/**
 * useAppInit
 * -----------------------------
 * A root-level initialization hook that bootstraps
 * global application behavior. This hook is intended to
 * be called only once inside the root <App /> component.
 *
 * Responsibilities:
 * 1. Load the Google reCAPTCHA script (Enterprise / v2 / v3)
 * 2. Load website configuration from server/local environment
 * 3. Initialize Kuick analytics for page tracking and events
 * 4. Dispatch BASIC_DINE_DATA to hydrate essential Redux state
 * 5. Smoothly scroll the application container to the top
 *
 * This hook centralizes all "app startup effects" to maintain
 * cleaner code inside the main App component.
 *
 * Usage:
 *   function App() {
 *     useAppInit();
 *     return <Routes />;
 *   }
 *
 * Notes:
 * - All effects inside this hook are intentionally designed
 *   to run only once at application startup.
 * - Avoid adding UI-related logic here; only use this hook
 *   for global initialization or environment setup.
 */

import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { useLoadRecaptchaScript } from "./useLoadRecaptchaScript";
import { useSetWebsiteConfig } from "./useSetWebsiteConfig";
import { useKuickAnalytics } from "./useAnalytics";
import { useBasicDineData } from "./useBasicDineData";

export const useAppInit = () => {
  const dispatch = useDispatch();
  // Load external scripts or async platform-level dependencies
  useLoadRecaptchaScript();

  // Load website-brand configuration (theme, type, domain config, etc.)
  useSetWebsiteConfig();

  // Initialize analytics listeners and tracking metadata
  useKuickAnalytics();

  useBasicDineData();
  /**
   * Scroll root container into view on initial app load.
   * This ensures the UI always starts at the top, especially
   * during SPA route transitions where scroll position may persist.
   */
  useEffect(() => {
    const root = document.querySelector("#root");

    // Optional safety check
    if (!root) return;

    root.scrollIntoView(
      {
        behavior: "smooth",
      },
      500
    );
  }, []);

  /**
   * Dispatch core bootstrap Redux action.
   * BASIC_DINE_DATA is required by multiple screens (menu,
   * cart, recurring orders, dine-in features, etc.).
   */
  useEffect(() => {
    dispatch({
      type: "BASIC_DINE_DATA",
    });
  }, [dispatch]);

  useEffect(() => {
  if (
    navigator.userAgent.includes('Prerender') ||
    navigator.userAgent.includes('HeadlessChrome')
  ) {
    document.dispatchEvent(new Event('prerender-ready'));
  }
}, []);

};

export default useAppInit;
